import React from 'react';
import { BookOpen, TrendingUp, DollarSign, Home, AlertCircle, CheckCircle2, Info, ChevronLeft } from 'lucide-react';
import { AppView } from '../types';

interface RNORGuideProps { setView: (view: AppView) => void; }

const RNORGuide: React.FC<RNORGuideProps> = ({ setView }) => {
  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8">
      <button onClick={() => setView(AppView.GUIDES)}
        className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-indigo-600 transition mb-6">
        <ChevronLeft size={16} /> All Guides
      </button>
      {/* Hero */}
      <div className="bg-gradient-to-br from-emerald-700 to-teal-500 rounded-2xl p-8 mb-8 text-white shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <BookOpen size={32} />
          <span className="text-emerald-200 font-semibold text-sm uppercase tracking-wider">2026 NRI Relocation Blueprint</span>
        </div>
        <h1 className="text-2xl md:text-3xl font-extrabold leading-tight mb-3">
          The RNOR Tax Window: Your One-Time Opportunity to Restructure Wealth Tax-Free
        </h1>
        <p className="text-emerald-100 text-base">
          Most returning NRIs leave lakhs — sometimes crores — on the table by not acting during the
          2–3 year RNOR window. This is the definitive playbook used by Pune returnees.
        </p>
      </div>

      {/* Article Hero Visual */}
      <div className="bg-gradient-to-br from-slate-800 to-emerald-900 rounded-2xl h-64 mb-8 flex items-center justify-center relative overflow-hidden shadow-lg">
        <div className="absolute -top-10 -left-10 w-48 h-48 bg-emerald-500 rounded-full opacity-20 blur-2xl"></div>
        <div className="absolute -bottom-10 -right-10 w-56 h-56 bg-teal-500 rounded-full opacity-20 blur-2xl"></div>
        <div className="relative z-10 flex items-center gap-8">
          <div className="flex flex-col items-center gap-2">
            <div className="bg-white/10 border border-white/20 rounded-2xl p-5 backdrop-blur-sm">
              <TrendingUp size={40} className="text-emerald-300" />
            </div>
            <span className="text-white/60 text-xs font-semibold uppercase tracking-wide">US Stocks</span>
          </div>
          <div className="text-white/30 text-3xl font-thin">→</div>
          <div className="flex flex-col items-center gap-2">
            <div className="bg-white/10 border border-white/20 rounded-2xl p-5 backdrop-blur-sm">
              <DollarSign size={40} className="text-yellow-300" />
            </div>
            <span className="text-white/60 text-xs font-semibold uppercase tracking-wide">RNOR Window</span>
          </div>
          <div className="text-white/30 text-3xl font-thin">→</div>
          <div className="flex flex-col items-center gap-2">
            <div className="bg-white/10 border border-white/20 rounded-2xl p-5 backdrop-blur-sm">
              <Home size={40} className="text-teal-300" />
            </div>
            <span className="text-white/60 text-xs font-semibold uppercase tracking-wide">India Wealth</span>
          </div>
        </div>
        <div className="absolute bottom-4 left-0 right-0 text-center">
          <span className="text-white/40 text-xs font-medium tracking-widest uppercase">Your 2–3 Year Tax Advantage</span>
        </div>
      </div>

      {/* Intro */}
      <div className="mb-8">
        <p className="text-slate-600 text-base leading-relaxed">
          The RNOR (Resident but Not Ordinarily Resident) status is one of the most powerful and least
          understood privileges available to returning NRIs. For a window of 2–3 financial years, foreign-sourced
          income is not taxed in India. Combined with careful US filing, this creates a brief corridor where
          capital gains on US assets can legally escape taxation in both countries entirely. As one community
          member put it: <span className="italic text-slate-500">"This is the biggest perk of not having a GC or USC."</span>
        </p>
      </div>

      {/* Part 1: RNOR Basics */}
      <section className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-emerald-100 p-2 rounded-lg">
            <Info size={22} className="text-emerald-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800">Part 1: What is RNOR and How Long Does it Last?</h2>
        </div>
        <p className="text-slate-600 mb-4">
          Under the Indian Income Tax Act, residency status is determined each financial year (April–March).
          You qualify as RNOR if you have been an NRI for 9 of the past 10 years, or have been in India for
          729 days or less in the past 7 years. The status typically lasts 2–3 years after your return.
        </p>

        <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-emerald-600 text-white">
                <th className="text-left p-4 font-semibold">Tax Status</th>
                <th className="text-left p-4 font-semibold">Foreign Income Taxed in India?</th>
                <th className="text-left p-4 font-semibold">Typical Duration</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-white border-b border-slate-100">
                <td className="p-4 font-semibold text-slate-800">NRI (Non-Resident Indian)</td>
                <td className="p-4 text-slate-600"><span className="inline-block bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded">No</span></td>
                <td className="p-4 text-slate-600">Until you return</td>
              </tr>
              <tr className="bg-slate-50 border-b border-slate-100">
                <td className="p-4 font-semibold text-slate-800">RNOR (Resident but Not Ordinarily Resident)</td>
                <td className="p-4 text-slate-600"><span className="inline-block bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded">No</span></td>
                <td className="p-4 text-slate-600">2–3 years after return</td>
              </tr>
              <tr className="bg-white">
                <td className="p-4 font-semibold text-slate-800">ROR (Ordinary Resident)</td>
                <td className="p-4 text-slate-600"><span className="inline-block bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded">Yes — Global Income</span></td>
                <td className="p-4 text-slate-600">After RNOR window closes</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <div className="flex gap-3">
            <AlertCircle size={18} className="text-emerald-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-bold text-emerald-800 text-sm">The April Return Trick</p>
              <p className="text-emerald-700 text-sm">
                Returning to India in April (start of the Indian financial year) can effectively give you two full
                financial years of RNOR status instead of one. Community consensus: <em>"Returning in April can
                effectively give you two full years of tax-free foreign income."</em>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Part 2: US Stocks */}
      <section className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-emerald-100 p-2 rounded-lg">
            <TrendingUp size={22} className="text-emerald-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800">Part 2: Selling US Stocks During RNOR — The Zero-Tax Play</h2>
        </div>
        <p className="text-slate-600 mb-5">
          This is the strategy most financial advisors in India will not tell you about because they are not
          licensed for cross-border advice. During RNOR, if you are also classified as a Non-Resident Alien (NRA)
          for US tax purposes, capital gains on US stocks can be untaxed in both countries.
        </p>

        <div className="space-y-4 mb-5">
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
            <h3 className="font-bold text-slate-800 mb-3">The Two Conditions to Meet</h3>
            <div className="space-y-3">
              <div className="flex gap-3">
                <CheckCircle2 size={18} className="text-emerald-500 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-slate-700 text-sm">NRA for US Purposes</p>
                  <p className="text-slate-500 text-sm">You must have already surrendered your Green Card or H1-B status (i.e., you are not a US citizen or Green Card holder). File <strong>Form W-8BEN</strong> with your US broker to claim NRA status.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <CheckCircle2 size={18} className="text-emerald-500 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-slate-700 text-sm">RNOR for India Purposes</p>
                  <p className="text-slate-500 text-sm">Your foreign-sourced capital gains are outside India's tax net during RNOR. This means the US-sourced capital gain is not taxable in India.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
            <h3 className="font-bold text-slate-800 mb-2">The Dividend Exception</h3>
            <p className="text-slate-600 text-sm">
              Capital gains benefit from the NRA exemption, but <strong>dividends do not</strong>. US companies
              will withhold 30% on dividends paid to NRAs. The India–US tax treaty can reduce this to 15% if
              properly claimed, but it is not automatic. Factor this into your portfolio restructuring decisions.
            </p>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex gap-3">
            <AlertCircle size={18} className="text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-bold text-amber-800 text-sm">This Window Closes Permanently</p>
              <p className="text-amber-700 text-sm">
                Once you become an Ordinary Resident (ROR), all global income including gains from your
                US portfolio is taxable in India at slab rates. The window does not reopen. Plan your
                portfolio restructuring in Year 1 of your return, not Year 3.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Part 3: 401k */}
      <section className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-emerald-100 p-2 rounded-lg">
            <DollarSign size={22} className="text-emerald-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800">Part 3: The 401k Decision Framework</h2>
        </div>
        <p className="text-slate-600 mb-5">
          Your 401k is the most complex piece of your financial transition. There is no single right answer —
          the best strategy depends on your age, US tax treaties, and how much you have accumulated. Community
          members joke: <em>"Some folks forget they have a 401k and check the balance at 60+."</em> Don't let that be you.
        </p>

        <div className="space-y-3 mb-5">
          {[
            {
              num: 1,
              title: 'Leave It Until 59½',
              pros: 'Simplest option. Avoid the 10% early withdrawal penalty. Let it compound.',
              cons: 'Money is locked. US tax applies on all withdrawals at ordinary income rates whenever you draw.',
              best: 'Age 50+ or large balances where penalty would be significant.'
            },
            {
              num: 2,
              title: 'Withdraw During RNOR (The Aggressive Play)',
              pros: 'No India tax during RNOR. Potentially the lowest combined tax scenario.',
              cons: '10% US early withdrawal penalty + 30% US withholding tax if under 59½. Net receipt is only ~60% of withdrawal.',
              best: 'Only if you have a small 401k balance and the penalty is an acceptable price for simplicity.'
            },
            {
              num: 3,
              title: 'Gradual Withdrawal Across Years',
              pros: 'Stay in lower US tax brackets by spreading withdrawals. File Form 10EE in India to defer India tax on 401k growth.',
              cons: 'Requires ongoing US tax filing. More complex to manage.',
              best: 'Large balances (₹1Cr+). Most recommended by the community for high-earners.'
            },
          ].map((item) => (
            <div key={item.num} className="bg-white border border-slate-200 rounded-xl p-5 flex gap-4 shadow-sm">
              <div className="bg-emerald-600 text-white w-7 h-7 rounded-full flex items-center justify-center font-bold text-sm shrink-0 mt-0.5">
                {item.num}
              </div>
              <div className="space-y-1.5">
                <p className="font-bold text-slate-800">{item.title}</p>
                <p className="text-xs text-slate-500"><span className="font-semibold text-emerald-700">Pros:</span> {item.pros}</p>
                <p className="text-xs text-slate-500"><span className="font-semibold text-red-600">Cons:</span> {item.cons}</p>
                <p className="text-xs text-indigo-700 font-medium">Best for: {item.best}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-blue-800 text-sm">
            <strong>Form 10EE:</strong> File this with the Indian Income Tax department to elect that
            retirement account income (including 401k withdrawals) is taxed in the year of withdrawal
            rather than the year of accrual. This is a critical form that many CAs in India are not aware of.
          </p>
        </div>
      </section>

      {/* Part 4: US Home */}
      <section className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-emerald-100 p-2 rounded-lg">
            <Home size={22} className="text-emerald-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800">Part 4: The US Home Decision — Sell or Keep?</h2>
        </div>
        <p className="text-slate-600 mb-5">
          The community is split, but the majority sold — and most say "peace over anything." Remote property
          management in the US from India is genuinely difficult: time zones, contractors, weather events,
          insurance complexity, and a depreciating dollar combine to make it stressful.
        </p>

        <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-emerald-600 text-white">
                <th className="text-left p-4 font-semibold">Factor</th>
                <th className="text-left p-4 font-semibold">Sell</th>
                <th className="text-left p-4 font-semibold">Keep & Rent</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-white border-b border-slate-100">
                <td className="p-4 font-semibold text-slate-800 align-top">Tax Timing</td>
                <td className="p-4 text-slate-600 align-top">Sell during RNOR: $500K joint capital gains exclusion (Section 121) + no India tax on the gain.</td>
                <td className="p-4 text-slate-600 align-top">Rental income taxable in US + India (once ROR). Complex double-taxation filings.</td>
              </tr>
              <tr className="bg-slate-50 border-b border-slate-100">
                <td className="p-4 font-semibold text-slate-800 align-top">Mental Load</td>
                <td className="p-4 text-slate-600 align-top">Clean break. Capital released for India investment.</td>
                <td className="p-4 text-slate-600 align-top">Property management fees (8–12%), maintenance, tenant issues across time zones.</td>
              </tr>
              <tr className="bg-white">
                <td className="p-4 font-semibold text-slate-800 align-top">Backup Plan</td>
                <td className="p-4 text-slate-600 align-top">No US re-entry asset base.</td>
                <td className="p-4 text-slate-600 align-top">Useful if return to US is possible (valid visa/GC). Locked-in low mortgage is valuable.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <div className="flex gap-3">
            <AlertCircle size={18} className="text-emerald-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-bold text-emerald-800 text-sm">The RNOR Window for Your Home Sale</p>
              <p className="text-emerald-700 text-sm">
                If you sell your US primary residence within 3 years of leaving it, you qualify for the
                Section 121 exclusion ($250K single / $500K joint). Combined with RNOR status in India,
                this can mean the entire capital gain is tax-free in both countries. This is a one-time
                window — once you lose primary residence status, it is gone.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Signature */}
      <div className="border-t border-slate-200 pt-6 mt-8 text-center">
        <p className="text-slate-400 text-xs uppercase tracking-widest font-semibold">Published by</p>
        <p className="text-indigo-700 font-extrabold text-lg tracking-wide mt-1">NRIGUIDE.BHARAT</p>
      </div>
    </div>
  );
};

export default RNORGuide;
